import Autocomplete from '@mui/material/Autocomplete'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Grid from '@mui/material/Grid'
import TextField from '@mui/material/TextField'
import { useIsFetching } from '@tanstack/react-query'
import KNAdvancedDropdown from 'components/KNAdvancedDropdown/KNAdvancedDropdown'
import { useVehiclesView } from 'context/VehiclesViewContext'
import { analyticsEvent } from 'global/helpers/analytics'
import { type ReactElement, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { VehicleType } from 'screens/VehicleManager/VehicleManager.types'
import { VEHICLES_QUERY_KEY } from './VehicleManager.service'

const VehicleFilters = (): ReactElement => {
  const { t } = useTranslation()
  const { filters, filtersDirty, setFilters, resetFilters } = useVehiclesView()
  const isFetching = useIsFetching({ queryKey: [VEHICLES_QUERY_KEY] })

  const handleResetClick = useCallback((): void => {
    resetFilters()
    analyticsEvent('polestar_cs_reset_vehicle_filters')
  }, [])

  return (
    <Box sx={{ position: 'relative' }}>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Autocomplete
            multiple
            freeSolo
            options={[]}
            value={filters.keywords}
            onChange={(event, newValue): void => {
              setFilters({
                keywords: newValue,
              })
              analyticsEvent('polestar_cs_vehicle_filters', ['keywords', newValue.join(', ')])
            }}
            renderValue={(tagValue, getTagProps): ReactElement[] =>
              tagValue.map((option, index) => (
                // eslint-disable-next-line react/jsx-key
                <Chip {...getTagProps({ index })} variant="outlined" size="small" label={option} />
              ))
            }
            renderInput={(params): ReactElement => (
              <TextField {...params} label={t('screens.cs.vehicle_manager.filters.search')} />
            )}
            disabled={isFetching > 0}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 3 }}>
          <KNAdvancedDropdown
            label={t('screens.cs.vehicle_manager.filters.vehicle_type')}
            options={Object.values(VehicleType).map((type) => ({
              value: type,
              label: t(`screens.cs.vehicle_manager.vehicle_types.${type}`),
            }))}
            value={filters.vehicleTypes}
            onChange={(newValue): void => {
              setFilters({
                vehicleTypes: newValue as VehicleType[],
              })
            }}
            onDelete={(value) => {
              setFilters({
                vehicleTypes: filters.vehicleTypes.filter((vehicleType) => vehicleType !== value),
              })
            }}
            multiple
            disabled={isFetching > 0}
          />
        </Grid>
      </Grid>
      {filtersDirty && (
        <Button
          size="small"
          variant="text"
          color="primary"
          onClick={handleResetClick}
          sx={{
            padding: 0,
            minHeight: 'auto',
          }}
        >
          {t('general.reset_filters')}
        </Button>
      )}
    </Box>
  )
}

export default VehicleFilters
